import React, { useState } from 'react'
import { PlusIcon, TrashIcon, XIcon } from 'lucide-react'
import type { BillingMilestone } from '../../types/agreement'
import {
  computeMilestoneAmounts,
  createEmptyMilestone,
  sumMilestonePercentages,
} from '../../lib/milestoneBilling'
import {
  applyMilestoneTemplate,
  customizeMilestoneScheduleCompletely,
} from '../../lib/billingMilestoneTemplates'
import { useBillingMilestoneTemplates } from '../../hooks/useBillingMilestoneTemplates'

interface Props {
  milestones: BillingMilestone[]
  totalAmount: number
  onChange: (milestones: BillingMilestone[]) => void
  readOnly?: boolean
  className?: string
  /** Show template picker / save-as (default true when not readOnly). */
  enableTemplates?: boolean
}

export default function MilestoneScheduleEditor({
  milestones,
  totalAmount,
  onChange,
  readOnly = false,
  className,
  enableTemplates = true,
}: Props) {
  const computed = computeMilestoneAmounts(totalAmount, milestones)
  const totalPct = sumMilestonePercentages(milestones)
  const pctOk = Math.abs(totalPct - 100) < 0.01
  const showTemplates = enableTemplates && !readOnly

  const { templates, saveScheduleAsTemplate } = useBillingMilestoneTemplates()
  const [templateMessage, setTemplateMessage] = useState<string | null>(null)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)

  const updateRow = (index: number, patch: Partial<BillingMilestone>) => {
    const next = milestones.map((m, i) => (i === index ? { ...m, ...patch } : m))
    onChange(computeMilestoneAmounts(totalAmount, next))
  }

  const addRow = () => {
    onChange([...milestones, createEmptyMilestone(milestones.length)])
  }

  const removeRow = (index: number) => {
    onChange(milestones.filter((_, i) => i !== index))
  }

  const handleTemplateSelect = (value: string) => {
    setTemplateMessage(null)
    if (!value) return

    if (value === '__customize__') {
      const result = customizeMilestoneScheduleCompletely(totalAmount, milestones)
      if (result.error) {
        setTemplateMessage(result.error)
        return
      }
      onChange(result.milestones)
      return
    }

    const tpl = templates.find((t) => t.id === value)
    if (!tpl) return
    const result = applyMilestoneTemplate(tpl.milestones, totalAmount, milestones)
    if (result.error) {
      setTemplateMessage(result.error)
      return
    }
    onChange(result.milestones)
  }

  const openSaveModal = () => {
    setSaveName('')
    setSaveDescription('')
    setTemplateMessage(null)
    setSaveModalOpen(true)
  }

  const confirmSaveTemplate = async () => {
    setSavingTemplate(true)
    setTemplateMessage(null)
    const result = await saveScheduleAsTemplate(milestones, saveName, saveDescription)
    setSavingTemplate(false)
    if (!result.ok) {
      setTemplateMessage(result.error || 'Failed to save template.')
      return
    }
    setSaveModalOpen(false)
    setTemplateMessage(`Template "${saveName.trim()}" saved.`)
  }

  return (
    <div className={className} data-consistency-anchor="milestone-schedule">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-sm font-medium text-gray-700">Milestone schedule</h4>
        {!readOnly && (
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <PlusIcon className="h-4 w-4" />
            Add milestone
          </button>
        )}
      </div>

      {showTemplates && (
        <div className="mb-4 space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Apply template
              </label>
              <select
                defaultValue=""
                onChange={(e) => {
                  handleTemplateSelect(e.target.value)
                  e.target.value = ''
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="">Choose a template…</option>
                <option value="__customize__">Customize completely</option>
                <optgroup label="Built-in presets">
                  {templates
                    .filter((t) => t.source === 'builtin')
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </optgroup>
                {templates.some((t) => t.source === 'saved') && (
                  <optgroup label="Saved templates">
                    {templates
                      .filter((t) => t.source === 'saved')
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </div>
            <button
              type="button"
              onClick={openSaveModal}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Save as template
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Pick a preset or saved template, or customize completely. You can still edit rows after
            applying.
          </p>
          {templateMessage && (
            <p
              className={`text-sm ${
                templateMessage.startsWith('Template')
                  ? 'text-green-700'
                  : 'text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5'
              }`}
            >
              {templateMessage}
            </p>
          )}
        </div>
      )}

      {milestones.length === 0 ? (
        <p className="text-sm text-gray-500">
          {showTemplates
            ? 'Choose a template above, or Customize completely / Add milestone to build your own schedule (must sum to 100%).'
            : 'Add milestones that sum to 100% of the project total.'}
        </p>
      ) : (
        <div className="space-y-3">
          {computed.map((m, index) => (
            <div
              key={m.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border border-gray-200 rounded-lg p-3 bg-white"
            >
              <div className="md:col-span-5">
                <label className="block text-xs font-medium text-gray-500 mb-1">Label</label>
                {readOnly ? (
                  <p className="text-sm text-gray-900">{m.label}</p>
                ) : m.status === 'invoiced' || m.status === 'paid' ? (
                  <p className="text-sm text-gray-900">{m.label}</p>
                ) : (
                  <input
                    type="text"
                    value={m.label}
                    onChange={(e) => updateRow(index, { label: e.target.value })}
                    placeholder="e.g. Mobilization"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">%</label>
                {readOnly || m.status === 'invoiced' || m.status === 'paid' ? (
                  <p className="text-sm text-gray-900">{m.percentage}%</p>
                ) : (
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={m.percentage || ''}
                    onChange={(e) =>
                      updateRow(index, { percentage: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                )}
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
                <p className="text-sm font-medium text-gray-900">${m.amount.toFixed(2)}</p>
              </div>
              <div className="md:col-span-2 flex justify-end items-center gap-2">
                {(m.status === 'invoiced' || m.status === 'paid') && (
                  <span className="text-xs uppercase tracking-wide text-gray-500">{m.status}</span>
                )}
                {!readOnly && milestones.length > 1 && m.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="p-2 text-red-600 hover:text-red-800"
                    aria-label="Remove milestone"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
        <span className={`font-medium ${pctOk ? 'text-green-700' : 'text-red-600'}`}>
          Total: {totalPct.toFixed(1)}% {pctOk ? '(complete)' : '(must equal 100%)'}
        </span>
        <span className="text-gray-500">
          Project total: ${Number(totalAmount || 0).toFixed(2)}
        </span>
        {!pctOk && totalPct < 100 && (
          <span className="text-gray-500">Remaining: {(100 - totalPct).toFixed(1)}%</span>
        )}
      </div>

      {saveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !savingTemplate && setSaveModalOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Save as template</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Save this milestone schedule for reuse on other contracts and proposals.
                </p>
              </div>
              <button
                type="button"
                disabled={savingTemplate}
                onClick={() => setSaveModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g. Standard 30/70"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={saveDescription}
                  onChange={(e) => setSaveDescription(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                disabled={savingTemplate}
                onClick={() => setSaveModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingTemplate || !saveName.trim()}
                onClick={confirmSaveTemplate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {savingTemplate ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
